import { createContext, use, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'

import type { ApiClient } from '@/shared/api/client'
import { useApiClient } from '@/shared/api/use-api-client'
import { useAuth } from '@/shared/auth/auth-context'
import { useSession } from '@/shared/auth/session'
import { isAdminHomeLink, LinkAccessProvider, type LinkAccess } from '@/shared/registry'

import { administers, holds, myPermissionsPath, noPermissions, parseMyPermissions, reachesEverything, reachesOthers, samePermissions, type MyPermissions, type PermissionId } from './permissions'

/** What the caller may do, as the UI shows or hides it. The API still checks every call. */
export interface Access {
  /** `loading` until the API answers; nothing passes until then, nor after an `error`. */
  readonly status: 'loading' | 'ready' | 'error'
  /** Whether the caller holds the permission at any scope, or at `scope` when given, such as `allOrganizations`, or at one of several. */
  readonly can: (permission: PermissionId, scope?: string | readonly string[]) => boolean
  /**
   * Whether the caller holds the permission at a scope that may reach this person's record, by
   * User Profile id: own scope reaches only the caller's own.
   */
  readonly canFor: (permission: PermissionId, userProfileId: string) => boolean
  /** Whether the permission reaches every record: Super User, or held at all organizations or with no scope. */
  readonly reachesEverything: (permission: PermissionId) => boolean
  /**
   * Whether the caller holds the permission as an administrator, as an Admin Home link needs:
   * with no scope, or at a scope that reaches other people's records and reaches someone.
   */
  readonly administers: (permission: PermissionId, scope?: string | readonly string[]) => boolean
  /** Whether the caller may assign any role to anyone. */
  readonly mayAssignRoles: boolean
  /** Whether every permission passes, as for Super User, who may act on any site (110552). */
  readonly hasAllPermissions: boolean
  /** Whether the caller holds the Instructor role, whether or not they teach a class (112024). */
  readonly isInstructor: boolean
}

function accessOf(status: Access['status'], mine: MyPermissions, self: string | null = null): Access {
  return {
    status,
    can: (permission, scope) => holds(mine, permission, scope),
    canFor: (permission, userProfileId) => (userProfileId === self ? holds(mine, permission) : reachesOthers(mine, permission)),
    reachesEverything: (permission) => reachesEverything(mine, permission),
    administers: (permission, scope) => administers(mine, permission, scope),
    mayAssignRoles: mine.mayAssignRoles,
    hasAllPermissions: mine.hasAllPermissions,
    isInstructor: mine.isInstructor,
  }
}

const AccessContext = createContext<Access>(accessOf('loading', noPermissions))

type Loaded = { readonly client: ApiClient; readonly sessionKey: string | null } & (
  | { readonly status: 'ready'; readonly mine: MyPermissions }
  | { readonly status: 'error' }
)

/** How long the caller's permissions stand before their next page or return to the tab reads them again. */
export const permissionsRefreshMilliseconds = 60_000

/**
 * Reads the caller's permissions once they are signed in, again whenever their session changes,
 * and hides every nav item and Admin Home link whose `permission` they lack. Access Control's
 * app-wide provider. A role, or a first or last person managed, that changes during the session
 * shows on the next page or return to the tab once a minute has passed (110599); never on a
 * timer, which would keep an idle session alive.
 */
export function AccessProvider(props: { readonly children: ReactNode }) {
  const { isAuthenticated, isResolving } = useAuth()
  const session = useSession()
  const client = useApiClient()
  const { pathname } = useLocation()
  const [loaded, setLoaded] = useState<Loaded | null>(null)
  // The API knows the caller only once the site's session exists; each new one reads again.
  const signedIn = isAuthenticated && !isResolving && session.status === 'signedIn'
  const sessionKey = session.session?.userProfileId ?? null
  // Reads again when the last read is a minute old; nothing while signed out.
  const readIfStale = useRef<() => void>(() => undefined)

  useEffect(() => {
    if (!signedIn) {
      readIfStale.current = () => undefined
      return undefined
    }
    const controller = new AbortController()
    // One read at a time, so a slow answer never overwrites a newer one.
    let isReading = false
    let readAt = 0
    const read = async () => {
      if (isReading) {
        return
      }
      isReading = true
      readAt = Date.now()
      try {
        const mine = parseMyPermissions(await client.get(myPermissionsPath, controller.signal))
        // An unchanged answer keeps the last one, so nothing that depends on it runs again.
        setLoaded((last) => (last?.status === 'ready' && samePermissions(last.mine, mine) ? last : { client, sessionKey, status: 'ready', mine }))
      } catch {
        // The client has logged a failed call. A failed first read shows no guarded entry; a failed re-read keeps what was known.
        if (!controller.signal.aborted) {
          setLoaded((last) => last ?? { client, sessionKey, status: 'error' })
        }
      } finally {
        isReading = false
      }
    }
    readIfStale.current = () => {
      if (Date.now() - readAt >= permissionsRefreshMilliseconds) {
        void read()
      }
    }
    void read()
    const onFocus = () => readIfStale.current()
    window.addEventListener('focus', onFocus)
    return () => {
      controller.abort()
      window.removeEventListener('focus', onFocus)
      readIfStale.current = () => undefined
      // A session restarted for the same person waits for its own answer, not the last session's.
      setLoaded(null)
    }
  }, [client, signedIn, sessionKey])

  // A new page is the caller at work, so it may read again.
  const shownPath = useRef(pathname)
  useEffect(() => {
    if (shownPath.current !== pathname) {
      shownPath.current = pathname
      readIfStale.current()
    }
  }, [pathname])

  // An answer read with another session's client is not this caller's.
  const current = signedIn && loaded?.client === client && loaded.sessionKey === sessionKey ? loaded : null
  const access = useMemo(
    () =>
      current === null
        ? accessOf('loading', noPermissions)
        : current.status === 'ready'
          ? accessOf('ready', current.mine, current.sessionKey)
          : accessOf('error', noPermissions),
    [current],
  )
  const canSee = useCallback<LinkAccess>(
    (entry, context) => {
      if (isAdminHomeLink(entry) && entry.isSuperUserOnly === true) {
        return access.hasAllPermissions
      }
      if ('isInstructorOnly' in entry && entry.isInstructorOnly === true && !access.isInstructor) {
        return false
      }
      if (entry.permission === undefined) {
        return true
      }
      // Admin Home is for administrators: an own or relationship grant never shows its links (110589).
      if (isAdminHomeLink(entry)) {
        return access.administers(entry.permission, entry.permissionScope)
      }
      // On a person's own record, own scope counts; on anyone else's, only a scope that may reach them.
      const personId = 'isPersonRecord' in entry && entry.isPersonRecord === true ? context?.recordId : undefined
      return [entry.permission].flat().some((permission) => (personId === undefined ? access.can(permission, entry.permissionScope) : access.canFor(permission, personId)))
    },
    [access],
  )

  return (
    <AccessContext value={access}>
      <LinkAccessProvider value={canSee} status={access.status}>{props.children}</LinkAccessProvider>
    </AccessContext>
  )
}

/** What the caller may do. Outside {@link AccessProvider} nothing passes. */
export function useAccess(): Access {
  return use(AccessContext)
}

/** How long a save waits for the caller's permissions before it gives up. */
const accessWaitMs = 20_000

/** The caller's permissions could not be read, or were still loading when a save gave up waiting for them. */
export class AccessUnknownError extends Error {
  constructor() {
    super('The caller’s permissions did not load.')
    this.name = 'AccessUnknownError'
  }
}

/**
 * Waits until the API has answered the caller's permissions, and resolves with them: for a save
 * that must not decide on permissions it does not know. Rejects with `AccessUnknownError` when the
 * read failed, or is still loading after `accessWaitMs`, so the save ends rather than waiting for good.
 */
export function useWhenAccessKnown(): () => Promise<Access> {
  const access = useAccess()
  const latest = useRef(access)
  const waiting = useRef(new Set<(known: Access) => void>())
  useEffect(() => {
    latest.current = access
    if (access.status !== 'loading') {
      for (const settle of waiting.current) {
        settle(access)
      }
      waiting.current.clear()
    }
  }, [access])
  return useCallback(async () => {
    const known = latest.current.status === 'loading'
      ? await new Promise<Access>((resolve, reject) => {
          const settle = (answered: Access) => {
            clearTimeout(timer)
            resolve(answered)
          }
          const timer = setTimeout(() => {
            waiting.current.delete(settle)
            reject(new AccessUnknownError())
          }, accessWaitMs)
          waiting.current.add(settle)
        })
      : latest.current
    if (known.status === 'error') {
      throw new AccessUnknownError()
    }
    return known
  }, [])
}

/** Whether the caller holds the permission at any scope, or at `scope` (or one of several) when given; `false` until the API answers. */
export function usePermission(permission: PermissionId, scope?: string | readonly string[]): boolean {
  return useAccess().can(permission, scope)
}

/** Whether the caller holds the permission at a scope that may reach this person's record; own scope reaches only their own. `false` until the API answers. */
export function usePermissionFor(permission: PermissionId, userProfileId: string): boolean {
  return useAccess().canFor(permission, userProfileId)
}

/** Shows its children, such as an action's button, only to a caller who holds the permission, at `scope` (or one of several) when given. */
export function Permitted(props: { readonly permission: PermissionId; readonly scope?: string | readonly string[]; readonly children: ReactNode }) {
  return usePermission(props.permission, props.scope) ? props.children : null
}
